/**
 * Mini-store public d'applications mobiles — page accessible sans auth.
 *
 * Design : inspiration Google Play Store — flat, blanc dominant, vert d'accent.
 * Pas de gradients, pas d'effets exagérés. Lisibilité avant tout.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Download, Smartphone, Users, GraduationCap, Briefcase, Globe2,
  CheckCircle2, ExternalLink, Loader2, Search, ChevronDown,
  MessageCircle, Mail, QrCode, X,
} from 'lucide-react';

interface AppItem {
  id: number;
  slug: string;
  nom: string;
  description_courte: string;
  audience: 'parent' | 'formateur' | 'etudiant' | 'public';
  audience_label: string;
  version: string;
  taille_humaine: string;
  min_android: string;
  icone_url: string | null;
  apk_url: string | null;
  a_un_apk: boolean;
  derniere_publication: string | null;
  nb_telechargements: number;
}

const AUDIENCE_ICON = {
  parent: Users,
  formateur: Briefcase,
  etudiant: GraduationCap,
  public: Globe2,
};

// Couleurs solides discrètes (background de l'icône fallback)
const AUDIENCE_BG: Record<string, string> = {
  parent:    '#1e88e5',  // bleu
  formateur: '#f59e0b',  // amber
  etudiant:  '#10b981',  // emerald
  public:    '#64748b',  // slate
};

// Vert Play Store officiel (#01875f) — pour le bouton Installer
const PLAY_GREEN = '#01875f';
const PLAY_GREEN_HOVER = '#017051';

export default function AppsStore() {
  const [apps, setApps] = useState<AppItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [qrApp, setQrApp] = useState<AppItem | null>(null);
  const [openHelp, setOpenHelp] = useState<string | null>('parent');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/public/apps/');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setApps(data.results || []);
      } catch (e: any) {
        setError(
          e?.message?.includes('Failed to fetch')
            ? 'Connexion réseau impossible. Vérifiez votre connexion internet.'
            : 'Impossible de charger les applications. Réessayez plus tard.',
        );
        setApps([]);
      } finally {
        setLoading(false);
      }
    })();
    // Trace de visite (analytics) — best effort, ignoré si échec
    fetch('/api/public/apps/ping/', { method: 'POST' }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter(a =>
      a.nom.toLowerCase().includes(q)
      || a.description_courte.toLowerCase().includes(q)
      || a.audience_label.toLowerCase().includes(q)
    );
  }, [apps, search]);

  const handleDownload = (app: AppItem) => {
    if (!app.a_un_apk) return;
    setDownloadingId(app.id);
    const link = document.createElement('a');
    link.href = `/api/public/apps/${app.slug}/telecharger/`;
    link.download = '';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setDownloadingId(null), 1800);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HEADER ─────────────────────────────────────────────────────────── */}
      <header className="border-b border-slate-200 sticky top-0 bg-white z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <img src="/logo.png" alt="2i" className="w-9 h-9 rounded-lg p-0.5 bg-white border border-slate-200 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-slate-900 leading-tight">Institut International 2i</div>
            <div className="text-xs text-slate-500 leading-tight">Applications officielles</div>
          </div>
        </div>
      </header>

      {/* HERO ─────────────────────────────────────────────────────────── */}
      <section className="border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3">
            Téléchargez nos applications
          </h1>
          <p className="text-base text-slate-600 max-w-2xl leading-relaxed">
            Suivez la scolarité de votre enfant, gérez vos cours ou consultez vos résultats depuis votre téléphone.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm text-slate-700 px-3 py-1.5 bg-slate-100 rounded-md">
            <Globe2 size={14} className="text-slate-500" />
            <span>Disponible aussi en version web — pas besoin d'installer</span>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-10">

        {/* RECHERCHE */}
        {apps.length > 3 && (
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une application…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 focus:outline-none" />
          </div>
        )}

        {/* GRID APPS */}
        {loading ? (
          <div className="text-center py-16 text-slate-400">
            <Loader2 size={28} className="inline animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-medium">{error}</p>
            <button onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 text-xs bg-red-600 hover:bg-red-700 text-white rounded font-semibold">
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Smartphone size={40} className="inline mb-3 text-slate-300" />
            <p className="text-sm">Aucune application disponible pour l'instant.</p>
            <p className="text-xs mt-1 text-slate-400">Revenez bientôt — de nouvelles applis arrivent.</p>
          </div>
        ) : (
          <div>
            <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
              Applications
            </h2>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
              {filtered.map(app => (
                <AppRow key={app.id} app={app}
                  downloading={downloadingId === app.id}
                  onDownload={() => handleDownload(app)}
                  onShowQR={() => setQrApp(app)} />
              ))}
            </div>
          </div>
        )}

        {/* COMMENT INSTALLER */}
        {apps.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
              Comment installer une application Android ?
            </h2>
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <ol className="space-y-4 text-sm text-slate-700">
                <Step n={1} t="Téléchargez l'APK">
                  Cliquez sur « Installer » sur l'application souhaitée. Le fichier <code className="text-[12px] bg-slate-100 px-1.5 py-0.5 rounded">.apk</code> sera enregistré sur votre téléphone.
                </Step>
                <Step n={2} t="Autorisez l'installation">
                  Au premier lancement, Android vous demandera la permission.
                  Allez dans <strong>Paramètres → Sécurité → Sources inconnues</strong> et activez pour votre navigateur (Chrome, Firefox…).
                </Step>
                <Step n={3} t="Ouvrez le fichier et installez">
                  Dans vos téléchargements, ouvrez le <code className="text-[12px] bg-slate-100 px-1.5 py-0.5 rounded">.apk</code>, puis appuyez sur « Installer ». L'application apparaîtra sur votre écran d'accueil.
                </Step>
                <Step n={4} t="Connectez-vous">
                  Lancez l'application et utilisez les identifiants fournis par la scolarité (voir section « Comment ouvrir un compte »).
                </Step>
              </ol>
            </div>
          </section>
        )}

        {/* COMMENT OUVRIR UN COMPTE */}
        <section>
          <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
            Comment ouvrir un compte ?
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
            <AccordionHelp
              open={openHelp === 'parent'}
              onToggle={() => setOpenHelp(openHelp === 'parent' ? null : 'parent')}
              icon={<Users size={18} className="text-blue-600" />}
              titre="Parent ou tuteur d'un étudiant"
            >
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                <li>Téléchargez l'application <strong>« Institut 2i — Parents »</strong> ci-dessus.</li>
                <li>Sur l'écran d'accueil, cliquez sur <strong>« Créer un compte »</strong> et renseignez votre nom, prénom, numéro de téléphone et adresse email.</li>
                <li>Ajoutez le <strong>matricule de votre enfant</strong> (visible sur sa carte d'étudiant ou son bulletin) et précisez votre lien (père, mère, tuteur).</li>
                <li>La scolarité valide votre inscription sous 48 h. Vous recevrez une notification par SMS ou email dès l'activation.</li>
                <li>Connectez-vous et accédez aux notes, présences, scans de copies, EDT, messages, paiements, etc.</li>
              </ol>
              <p className="mt-3 text-xs text-slate-500">
                Astuce : un même parent peut lier plusieurs enfants depuis l'application.
              </p>
            </AccordionHelp>

            <AccordionHelp
              open={openHelp === 'formateur'}
              onToggle={() => setOpenHelp(openHelp === 'formateur' ? null : 'formateur')}
              icon={<Briefcase size={18} className="text-amber-600" />}
              titre="Formateur / Enseignant"
            >
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                <li>Rendez-vous à la scolarité avec une <strong>pièce d'identité</strong> et l'attestation de votre matière.</li>
                <li>L'administration créera votre compte depuis la liste des enseignants déjà enregistrés.</li>
                <li>Vous recevrez par <strong>WhatsApp ou SMS</strong> votre identifiant et un mot de passe initial.</li>
                <li>Ouvrez l'Espace Formateur à l'adresse <code className="text-[12px] bg-slate-100 px-1.5 py-0.5 rounded">gestion-notes-2i.com/formateur</code>.</li>
                <li>Changez votre mot de passe à la première connexion et ajoutez votre photo de profil.</li>
              </ol>
              <p className="mt-3 text-xs text-slate-500">
                Votre identifiant est lié à votre fiche enseignant — toutes vos matières apparaîtront automatiquement.
              </p>
            </AccordionHelp>

            <AccordionHelp
              open={openHelp === 'etudiant'}
              onToggle={() => setOpenHelp(openHelp === 'etudiant' ? null : 'etudiant')}
              icon={<GraduationCap size={18} className="text-emerald-600" />}
              titre="Étudiant"
            >
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                <li>Rendez-vous sur le portail étudiant : <code className="text-[12px] bg-slate-100 px-1.5 py-0.5 rounded">gestion-notes-2i.com/portail</code>.</li>
                <li>À la première connexion, cliquez sur <strong>« Trouver mon matricule »</strong> et entrez votre nom + date de naissance.</li>
                <li>Définissez votre mot de passe et une question de sécurité.</li>
                <li>L'accès aux résultats est conditionné par la mise à jour des frais (vérifiable en temps réel).</li>
              </ol>
            </AccordionHelp>
          </div>
        </section>

        {/* CONTACT */}
        <section>
          <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
            Besoin d'aide ?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a href="https://wa.me/242000000000" target="_blank" rel="noreferrer"
              className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-emerald-400 transition-colors">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                style={{ background: '#25d366' }}>
                <MessageCircle size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">WhatsApp</div>
                <div className="text-xs text-slate-500">Réponse rapide</div>
              </div>
              <ExternalLink size={13} className="text-slate-400" />
            </a>
            <a href="mailto:contact@gestion-notes-2i.com"
              className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-blue-400 transition-colors">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white bg-blue-600">
                <Mail size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">Email</div>
                <div className="text-xs text-slate-500 truncate">contact@gestion-notes-2i.com</div>
              </div>
              <ExternalLink size={13} className="text-slate-400" />
            </a>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="border-t border-slate-100 pt-5 pb-2 text-center text-xs text-slate-400">
          Institut International 2i — Brazzaville, Congo
        </footer>
      </main>

      {qrApp && <QRModal app={qrApp} onClose={() => setQrApp(null)} />}
    </div>
  );
}

function AppRow({ app, downloading, onDownload, onShowQR }: {
  app: AppItem; downloading: boolean; onDownload: () => void; onShowQR: () => void;
}) {
  const Icon = AUDIENCE_ICON[app.audience] || Globe2;
  const bg = AUDIENCE_BG[app.audience] || AUDIENCE_BG.public;
  return (
    <article className="px-4 py-4 sm:px-5 flex items-start gap-3 sm:gap-4 hover:bg-slate-50 transition-colors">
      {app.icone_url ? (
        <img src={app.icone_url} alt=""
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover border border-slate-200 flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-white flex-shrink-0"
          style={{ background: bg }}>
          <Icon size={24} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-slate-900 truncate">{app.nom}</h3>
        <div className="text-xs text-slate-500 mt-0.5">
          {app.audience_label}
        </div>
        {app.description_courte && (
          <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-2">
            {app.description_courte}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
          {app.version && <span>Version {app.version}</span>}
          {app.taille_humaine && <span>·  {app.taille_humaine}</span>}
          {app.min_android && <span>·  Android {app.min_android}+</span>}
          {app.nb_telechargements > 0 && (
            <span>·  {formatNb(app.nb_telechargements)} téléch.</span>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 self-center flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5">
        <button
          onClick={onDownload}
          disabled={!app.a_un_apk || downloading}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: !app.a_un_apk ? '#94a3b8'
              : downloading ? '#10b981'
              : PLAY_GREEN,
          }}
          onMouseEnter={(e) => {
            if (app.a_un_apk && !downloading) e.currentTarget.style.backgroundColor = PLAY_GREEN_HOVER;
          }}
          onMouseLeave={(e) => {
            if (app.a_un_apk && !downloading) e.currentTarget.style.backgroundColor = PLAY_GREEN;
          }}
        >
          {!app.a_un_apk ? (
            <>Bientôt</>
          ) : downloading ? (
            <><CheckCircle2 size={14} /> En cours…</>
          ) : (
            <><Download size={14} /> Installer</>
          )}
        </button>
        {app.a_un_apk && (
          <button
            onClick={onShowQR}
            title="Afficher le QR code pour installer depuis un téléphone"
            className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50 transition-colors"
          >
            <QrCode size={14} />
            <span className="hidden sm:inline">QR</span>
          </button>
        )}
      </div>
    </article>
  );
}

function QRModal({ app, onClose }: { app: AppItem; onClose: () => void }) {
  const qrUrl = `/api/public/apps/${app.slug}/qrcode.png`;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Installer sur téléphone</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
            <X size={18} />
          </button>
        </header>
        <div className="p-6 flex flex-col items-center">
          <div className="bg-white p-3 rounded-lg border border-slate-200">
            <img src={qrUrl} alt={`QR code pour télécharger ${app.nom}`}
              className="w-64 h-64 block" />
          </div>
          <p className="mt-4 text-sm text-slate-700 text-center font-medium">
            {app.nom}
          </p>
          <p className="mt-1 text-xs text-slate-500 text-center leading-relaxed">
            Scannez ce QR code avec l'appareil photo de votre téléphone Android pour
            télécharger directement l'application.
          </p>
        </div>
        <footer className="px-5 py-3 border-t border-slate-200 text-center">
          <button onClick={onClose}
            className="text-sm font-medium text-slate-600 hover:text-slate-900">
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
}

function Step({ n, t, children }: { n: number; t: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3 items-start">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 text-slate-700 font-semibold text-sm flex items-center justify-center">
        {n}
      </div>
      <div className="flex-1 pt-0.5">
        <div className="font-semibold text-slate-900 text-sm mb-0.5">{t}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
      </div>
    </li>
  );
}

function AccordionHelp({ open, onToggle, icon, titre, children }: {
  open: boolean; onToggle: () => void;
  icon: React.ReactNode; titre: string; children: React.ReactNode;
}) {
  return (
    <div>
      <button onClick={onToggle}
        className="w-full px-4 py-3.5 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors">
        {icon}
        <span className="flex-1 font-semibold text-sm text-slate-900">{titre}</span>
        <ChevronDown size={16}
          className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-200 ease-out ${
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}>
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

function formatNb(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
